import { Outlet, useRouterState } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { cx } from "../../ui";
import { ToastProvider } from "../../ui/toast";
import { PushListener } from "../settings/PushListener";
import { SidebarCollapsed } from "./sidebar-state";
import { AccountHealthBanner } from "./AccountHealthBanner";
import { Sidebar } from "./Sidebar";
import { TopBar } from "./TopBar";

/**
 * Left sidebar + top bar + content. Below the `md` breakpoint the sidebar is a drawer: the menu
 * button opens it, Escape, the backdrop or a navigation closes it, and focus moves in and back.
 */
export function AppLayout() {
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = useCallback(() => {
    setCollapsed((c) => {
      writeCollapsed(!c);
      return !c;
    });
  }, []);
  const menuButton = useRef<HTMLButtonElement>(null);
  const firstInDrawer = useRef<HTMLSelectElement>(null);
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // A task page (WP-053) takes the width: the sidebar shows as its rail there unless the person
  // opens it, and that choice lasts until they leave the page (their saved preference is untouched).
  const taskPage = /^\/w\/[^/]+\/tasks\/[^/]+\/?$/.test(pathname);
  const [peek, setPeek] = useState(false);
  useEffect(() => setPeek(false), [pathname]);
  const rail = collapsed || (taskPage && !peek);
  const onToggleSidebar = taskPage && !collapsed ? () => setPeek((p) => !p) : toggleCollapsed;

  const close = useCallback(() => {
    setOpen(false);
    menuButton.current?.focus();
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    firstInDrawer.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <ToastProvider>
      <SidebarCollapsed.Provider value={rail}>
      <PushListener />
      <div className={cx("min-h-dvh bg-bg text-text md:grid", rail ? "md:grid-cols-[4rem_minmax(0,1fr)]" : "md:grid-cols-[16rem_minmax(0,1fr)]")}>
        <a
          href="#main"
          className="sr-only focus:not-sr-only focus:fixed focus:left-2 focus:top-2 focus:z-50 focus:rounded-md focus:bg-surface focus:px-3 focus:py-2"
        >
          Skip to content
        </a>
        <Sidebar open={open} collapsed={rail} onToggleCollapsed={onToggleSidebar} firstLinkRef={firstInDrawer} />
        {open ? <div className="fixed inset-0 z-30 bg-black/40 md:hidden" aria-hidden="true" onClick={close} /> : null}
        <div className="flex min-w-0 flex-col">
          <TopBar menuOpen={open} onMenu={() => setOpen(true)} menuButtonRef={menuButton} />
          <AccountHealthBanner pathname={pathname} />
          <main id="main" tabIndex={-1} className="flex-1 px-4 py-6 sm:px-6">
            {/* The inbox (split pane) and chat use the page's width; other pages keep a reading width. */}
            <div className={pathname.endsWith("/inbox") || /\/chat(\/|$)/.test(pathname) ? "mx-auto max-w-screen-2xl" : "mx-auto max-w-4xl"}>
              <Outlet />
            </div>
          </main>
        </div>
      </div>
      </SidebarCollapsed.Provider>
    </ToastProvider>
  );
}

const COLLAPSED_KEY = "kwp.sidebar.collapsed";

/** Whether the sidebar is collapsed to icons: a per-browser preference, so storage may be missing. */
function readCollapsed(): boolean {
  try {
    return window.localStorage.getItem(COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

function writeCollapsed(value: boolean): void {
  try {
    window.localStorage.setItem(COLLAPSED_KEY, value ? "1" : "0");
  } catch {
    // Not remembered in this browser; the toggle still works for the session.
  }
}
